import * as THREE from 'three';
import { CubeFace, TileCoordinate } from './Types';

type TextureTuning = {
  /** Prefer false unless your tile images are padded/extruded. */
  useMipmaps?: boolean;
  /** Pass renderer.capabilities.getMaxAnisotropy() from caller if you want. */
  anisotropy?: number;
  /** If true we clamp edges for standalone tile textures; if false we allow repeat for full-face. */
  forTiles?: boolean;
  /** Flip left/right (re-invert orientation). */
  invertHorizontal?: boolean;
  /** Optional: flip up/down. */
  invertVertical?: boolean;
};

export class CubeFaceGeometry {
  private static readonly FACE_SIZE = 500;               // radius-ish
  private static readonly EPS_GEOM_OVERLAP = 1.0005;     // minimal overlap to hide cracks
  private static readonly HALF = CubeFaceGeometry.FACE_SIZE;

  // ---------------- GEOMETRY (faces & tiles)

  /**
   * Create a cube geometry with 6 faces for tile-based rendering
   */
  public static createCubeGeometry(): THREE.BoxGeometry {
    // Inside-out cube: BackSide material will be used at the mesh/material level
    return new THREE.BoxGeometry(this.FACE_SIZE * 2, this.FACE_SIZE * 2, this.FACE_SIZE * 2);
  }

  /**
   * Create individual face geometry for tile-based rendering
   */
  public static createFaceGeometry(face: CubeFace): THREE.PlaneGeometry {
    const geometry = new THREE.PlaneGeometry(this.FACE_SIZE * 2, this.FACE_SIZE * 2);
    this.positionFaceGeometry(geometry, face);
    return geometry;
  }

  /**
   * Position face geometry in 3D space according to cube face
   */
  private static positionFaceGeometry(geometry: THREE.PlaneGeometry, face: CubeFace): void {
    // PlaneGeometry starts facing +Z. Rotate then translate to the cube shell.
    switch (face) {
      case CubeFace.FRONT:
        geometry.translate(0, 0, this.HALF);
        break;
      case CubeFace.BACK:
        geometry.rotateY(Math.PI);
        geometry.translate(0, 0, -this.HALF);
        break;
      case CubeFace.LEFT:
        geometry.rotateY(-Math.PI / 2);
        geometry.translate(-this.HALF, 0, 0);
        break;
      case CubeFace.RIGHT:
        geometry.rotateY(Math.PI / 2);
        geometry.translate(this.HALF, 0, 0);
        break;
      case CubeFace.UP:
        geometry.rotateX(-Math.PI / 2);
        geometry.translate(0, this.HALF, 0);
        break;
      case CubeFace.DOWN:
        geometry.rotateX(Math.PI / 2);
        geometry.translate(0, -this.HALF, 0);
        break;
    }
    geometry.computeBoundingSphere();
  }

  /**
   * Create tile geometry for a specific tile coordinate
   */
  public static createTileGeometry(
    coordinate: TileCoordinate,
    tilesPerFace: number,
  ): THREE.PlaneGeometry {
    const tileSize = (this.FACE_SIZE * 2) / tilesPerFace;
    const geometry = new THREE.PlaneGeometry(
      tileSize * this.EPS_GEOM_OVERLAP,
      tileSize * this.EPS_GEOM_OVERLAP,
    );

    const tilePosition = this.calculateTilePosition(coordinate, tilesPerFace);
    this.positionTileGeometry(geometry, coordinate.face, tilePosition);

    geometry.computeBoundingSphere();
    return geometry;
  }

  /**
   * Calculate tile position within a face
   */
  private static calculateTilePosition(
    coordinate: TileCoordinate,
    tilesPerFace: number,
  ): { x: number; y: number } {
    const tileSize = (this.FACE_SIZE * 2) / tilesPerFace;
    const x = -this.FACE_SIZE + tileSize * (coordinate.x + 0.5);
    const y = this.FACE_SIZE - tileSize * (coordinate.y + 0.5);
    return { x, y };
  }

  /**
   * Position tile geometry in 3D space
   */
  private static positionTileGeometry(
    geometry: THREE.PlaneGeometry,
    face: CubeFace,
    position: { x: number; y: number },
  ): void {
    switch (face) {
      case CubeFace.FRONT:
        geometry.translate(position.x, position.y, this.HALF);
        break;
      case CubeFace.BACK:
        geometry.rotateY(Math.PI);
        geometry.translate(-position.x, position.y, -this.HALF);
        break;
      case CubeFace.LEFT:
        geometry.rotateY(-Math.PI / 2);
        geometry.translate(-this.HALF, position.y, -position.x);
        break;
      case CubeFace.RIGHT:
        geometry.rotateY(Math.PI / 2);
        geometry.translate(this.HALF, position.y, position.x);
        break;
      case CubeFace.UP:
        geometry.rotateX(-Math.PI / 2);
        geometry.translate(position.x, this.HALF, position.y);
        break;
      case CubeFace.DOWN:
        geometry.rotateX(Math.PI / 2);
        geometry.translate(position.x, -this.HALF, -position.y);
        break;
    }
  }

  // ---------------- UV HELPERS (optional; useful if you atlas tiles)

  /**
   * Calculate texture coordinates for a tile
   */
  public static calculateTileUVs(coordinate: TileCoordinate, tilesPerFace: number): number[] {
    const s = 1.0 / tilesPerFace;
    const u0 = coordinate.x * s;
    const v0 = coordinate.y * s;
    const u1 = u0 + s;
    const v1 = v0 + s;

    // UV coordinates for a quad (two triangles): bottom-left, bottom-right, top-left, top-right
    return [u0, v1, u1, v1, u0, v0, u1, v0];
  }

  /** Shrink UVs by half a texel to reduce edge bleeding when no padding/extrusion exists. */
  public static shrinkUVsHalfTexel(geometry: THREE.PlaneGeometry, texture: THREE.Texture): void {
    const img = texture.image as { width?: number; height?: number } | undefined;
    if (!img?.width || !img?.height) return;

    const dx = 0.5 / img.width;
    const dy = 0.5 / img.height;
    const uv = geometry.getAttribute('uv') as THREE.BufferAttribute;

    for (let i = 0; i < uv.count; i++) {
      const u = uv.getX(i);
      const v = uv.getY(i);
      uv.setXY(
        i,
        THREE.MathUtils.clamp(u, dx, 1 - dx),
        THREE.MathUtils.clamp(v, dy, 1 - dy),
      );
    }
    uv.needsUpdate = true;
  }

  // ---------------- TEXTURE & MATERIAL

  /**
   * Apply basic texture settings for 360° cube mapping / tiles
   */
  public static applyTextureSettings(texture: THREE.Texture, opts: TextureTuning = {}): void {
    const { useMipmaps = false, anisotropy = 0, forTiles = true } = opts;

    // For individual tile images, clamping avoids sampling outside the image.
    // For single full-face panoramas, RepeatWrapping is fine.
    texture.wrapS = forTiles ? THREE.ClampToEdgeWrapping : THREE.RepeatWrapping;
    texture.wrapT = forTiles ? THREE.ClampToEdgeWrapping : THREE.RepeatWrapping;

    if (useMipmaps) {
      texture.minFilter = THREE.LinearMipmapLinearFilter;
      texture.generateMipmaps = true;
    } else {
      texture.minFilter = THREE.LinearFilter;
      texture.generateMipmaps = false;
    }
    texture.magFilter = THREE.LinearFilter;

    if (anisotropy > 0) texture.anisotropy = anisotropy;

    // Keep default flipY unless your loader/origin requires otherwise.
    // texture.flipY = false;

    texture.needsUpdate = true;
  }

  /**
   * Create basic material settings for 360° cube rendering (with optional inversion)
   */
  public static createSeamlessMaterial(
    texture: THREE.Texture,
    opts: TextureTuning = { forTiles: true, invertHorizontal: true }, // default: re-invert L/R
  ): THREE.MeshBasicMaterial {
    this.applyTextureSettings(texture, opts);

    // Re-invert orientation safely even with ClampToEdgeWrapping (works per tile as UV mirror).
    if (opts.invertHorizontal) {
      const repX = texture.repeat.x === 0 ? 1 : Math.abs(texture.repeat.x);
      texture.repeat.x = -repX;
      texture.offset.x = 1; // keep UVs in [0..1] after negative repeat
    }
    if (opts.invertVertical) {
      const repY = texture.repeat.y === 0 ? 1 : Math.abs(texture.repeat.y);
      texture.repeat.y = -repY;
      texture.offset.y = 1;
    }

    return new THREE.MeshBasicMaterial({
      map: texture,
      side: THREE.BackSide,
      transparent: false,
      // LOD z-fighting guard (bias towards camera)
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    });
  }

  // ---------------- MESH CREATION

  /**
   * Create a mesh for a specific tile with anti-seaming & inversion options
   */
  public static createTileMesh(
    coordinate: TileCoordinate,
    tilesPerFace: number,
    texture: THREE.Texture,
    materialOpts: TextureTuning = { forTiles: true, invertHorizontal: true },
  ): THREE.Mesh {
    const geometry = this.createTileGeometry(coordinate, tilesPerFace);

    // If your source tiles have no 1–2px padding/extrusion, shrink UVs a bit:
    this.shrinkUVsHalfTexel(geometry, texture);

    const material = this.createSeamlessMaterial(texture, materialOpts);
    const mesh = new THREE.Mesh(geometry, material);

    // Camera is inside the cube → disable frustum culling to avoid popping
    mesh.frustumCulled = false;

    // Draw higher LODs “on top” of coarser ones to reduce flicker while swapping.
    mesh.renderOrder = coordinate.level;

    // Stronger depth bias by level to minimize z-fighting during LOD transitions
    const mat = mesh.material as THREE.MeshBasicMaterial;
    mat.polygonOffset = true;
    mat.polygonOffsetFactor = -10;
    mat.polygonOffsetUnits = -coordinate.level;

    // Metadata for debugging/identification
    mesh.userData = {
      isTile: true,
      coordinate,
      tilesPerFace,
      level: coordinate.level,
      face: coordinate.face,
      tileKey: `${coordinate.face}_L${coordinate.level}_${coordinate.x}_${coordinate.y}`,
    };
    mesh.name = `tile-${coordinate.face}-L${coordinate.level}-${coordinate.x}-${coordinate.y}`;

    return mesh;
  }

  // ---------------- COORDINATE CONVERSIONS

  /**
   * Convert world coordinates to cube face coordinates
   */
  public static worldToCubeFace(worldPosition: THREE.Vector3): {
    face: CubeFace;
    u: number;
    v: number;
  } {
    const ax = Math.abs(worldPosition.x);
    const ay = Math.abs(worldPosition.y);
    const az = Math.abs(worldPosition.z);

    let face: CubeFace;
    let u: number;
    let v: number;

    if (ax >= ay && ax >= az) {
      if (worldPosition.x > 0) {
        face = CubeFace.RIGHT;
        u = -worldPosition.z / ax;
        v = worldPosition.y / ax;
      } else {
        face = CubeFace.LEFT;
        u = worldPosition.z / ax;
        v = worldPosition.y / ax;
      }
    } else if (ay >= az) {
      if (worldPosition.y > 0) {
        face = CubeFace.UP;
        u = worldPosition.x / ay;
        v = -worldPosition.z / ay;
      } else {
        face = CubeFace.DOWN;
        u = worldPosition.x / ay;
        v = worldPosition.z / ay;
      }
    } else {
      if (worldPosition.z > 0) {
        face = CubeFace.FRONT;
        u = worldPosition.x / az;
        v = worldPosition.y / az;
      } else {
        face = CubeFace.BACK;
        u = -worldPosition.x / az;
        v = worldPosition.y / az;
      }
    }

    // Normalize u, v to [0, 1] range
    return { face, u: (u + 1) * 0.5, v: (v + 1) * 0.5 };
  }

  /**
   * Convert cube face coordinates to tile coordinates
   */
  public static faceToTileCoordinate(
    face: CubeFace,
    u: number,
    v: number,
    tilesPerFace: number,
    level: number,
  ): TileCoordinate {
    const x = Math.max(0, Math.min(tilesPerFace - 1, Math.floor(u * tilesPerFace)));
    const y = Math.max(0, Math.min(tilesPerFace - 1, Math.floor(v * tilesPerFace)));
    return { x, y, face, level };
  }
}
